import { useState, useMemo, useCallback, useEffect } from 'react'
import useDealStore from '../store/dealStore'
import '../styles/module12.css'

// Task 10: Parse uploaded checklist (.docx/.xlsx) — simple text extraction
async function parseChecklistFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result || ''
      // Extract lines as checklist items (simple heuristic)
      const items = text
        .split(/\r?\n/)
        .map(l => l.replace(/^[\s\-•*\d.]+/, '').trim())
        .filter(l => l.length > 3 && l.length < 200)
        .map((label, i) => ({ id: `custom_${i}`, label, done: false, source: 'upload' }))
      resolve(items)
    }
    reader.readAsText(file)
  })
}

const money = (v) => `$${Math.round(v || 0).toLocaleString()}`
const today = () => new Date().toISOString().split('T')[0]
const addDays = (d, n) => {
  const dt = new Date(d)
  dt.setDate(dt.getDate() + n)
  return dt.toISOString().split('T')[0]
}
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

// ── Milestone definitions ─────────────────────────────────────────────────────
const MILESTONES = [
  { id: 'loiSubmitted', label: 'LOI Submitted', weight: 8, icon: '📝', desc: 'Letter of Intent submitted to seller' },
  { id: 'loiAccepted', label: 'LOI Accepted', weight: 10, icon: '', desc: 'Seller accepted the LOI and agreed to terms' },
  { id: 'psaSigned', label: 'PSA Signed', weight: 12, icon: '📋', desc: 'Purchase & Sale Agreement executed by both parties' },
  { id: 'escrowOpened', label: 'Escrow Opened', weight: 15, icon: '🏦', desc: 'Escrow account opened with title company' },
  { id: 'earnestDeposited', label: 'Earnest Money Deposited', weight: 10, icon: '💰', desc: 'Earnest money deposit wired to escrow' },
  { id: 'dueDiligence', label: 'Due Diligence', weight: 20, icon: '🔍', desc: 'Physical inspections, title search, financial review complete' },
  { id: 'financingApproval', label: 'Financing Approval', weight: 15, icon: '🏛️', desc: 'Lender issues formal loan approval / commitment letter' },
  { id: 'closingScheduled', label: 'Closing Scheduled', weight: 5, icon: '📅', desc: 'Closing date confirmed with all parties' },
  { id: 'closed', label: 'Closed', weight: 5, icon: '🎉', desc: 'Transaction closed, deed recorded, keys transferred' },
]

const STATUS = { done: 'done', active: 'active', pending: 'pending' }

function getMilestoneStatuses(completedIds) {
  let foundActive = false
  return MILESTONES.map((m) => {
    if (completedIds.includes(m.id)) return STATUS.done
    if (!foundActive) { foundActive = true; return STATUS.active }
    return STATUS.pending
  })
}

function calcProgress(completedIds) {
  const total = MILESTONES.reduce((s, m) => s + m.weight, 0)
  const done = MILESTONES.filter((m) => completedIds.includes(m.id)).reduce((s, m) => s + m.weight, 0)
  return Math.round((done / total) * 100)
}

// ── Risk analysis ─────────────────────────────────────────────────────────────
function buildRiskAnalysis(completedIds, escrowOpened) {
  const statuses = getMilestoneStatuses(completedIds)
  const activeIdx = statuses.indexOf(STATUS.active)
  const activeMilestone = MILESTONES[activeIdx]
  const progress = calcProgress(completedIds)

  const risks = []
  if (!completedIds.includes('dueDiligence'))
    risks.push({ level: 'Medium', label: 'Due Diligence', detail: 'Inspection & title items pending — schedule immediately to avoid closing delays.' })
  if (!completedIds.includes('financingApproval'))
    risks.push({ level: 'High', label: 'Financing Approval', detail: 'Lender commitment not yet received. Follow up with lender within 48 hrs.' })
  if (!completedIds.includes('earnestDeposited'))
    risks.push({ level: 'High', label: 'Earnest Money', detail: 'EMD not yet deposited — seller may have grounds to cancel contract.' })
  if (progress < 30)
    risks.push({ level: 'Low', label: 'Early Stage', detail: 'Deal is in early escrow phase. Set key dates and assign tasks in Module 9.' })

  return {
    overallRisk: risks.some(r => r.level === 'High') ? 'High' : risks.some(r => r.level === 'Medium') ? 'Medium' : 'Low',
    risks,
    nextStep: activeMilestone ? activeMilestone.desc : 'All milestones complete — ready to close!',
    nextMilestone: activeMilestone?.label || 'Closed',
    progress,
  }
}

// ── Title checks ──────────────────────────────────────────────────────────────
const TITLE_CHECKS = [
  { id: 'chainOfTitle', label: 'Chain of Title', detail: '30-year ownership history verified through county records.' },
  { id: 'ownerVerified', label: 'Ownership Verified', detail: 'Current owner confirmed on deed of record.' },
  { id: 'liensCleared', label: 'Liens & Encumbrances', detail: 'No outstanding mechanics liens, judgments, or attachments.' },
  { id: 'taxStatus', label: 'Property Tax Status', detail: 'Current taxes paid; no delinquencies or tax sale risk.' },
  { id: 'easements', label: 'Easements & Rights', detail: 'Access easements reviewed; no adverse impacts identified.' },
  { id: 'titleCommitment', label: 'Title Commitment Issued', detail: 'Title insurance commitment received from title company.' },
]

// ── Timeline events generator ─────────────────────────────────────────────────
function buildTimeline(completedIds, escrowStart, dealName) {
  const events = []
  MILESTONES.forEach((m) => {
    if (completedIds.includes(m.id)) {
      events.push({ date: escrowStart, label: m.label, type: 'done', icon: m.icon })
    }
  })
  if (!events.length) events.push({ date: today(), label: 'Escrow process initiated', type: 'active', icon: '🚀' })
  return events.reverse()
}

// ── Main Component ────────────────────────────────────────────────────────────
function Module12Page() {
  const { dealState, updateDealState, updateAvailableModules, forceNavigateToModule } = useDealStore()
  const raw = dealState.rawDealData || {}
  const psa = dealState.psaDocument || {}
  const loi = dealState.loiDocument || {}
  const score = dealState.scoreAndOffer || {}

  const propName = dealState.dealName || raw.location?.city ? `${raw.location?.city || ''} Property` : 'Subject Property'
  const location = [raw.location?.address, raw.location?.city, raw.location?.state].filter(Boolean).join(', ')
  const price = Number(raw.price) || Number(score.offerPrice) || 0
  const earnestAmt = Math.round(price * 0.01)  // 1% earnest money

  // ── Persisted escrow state (reads from dealState.escrowWorkflow) ──
  const saved = dealState.escrowWorkflow || {}

  const [escrowCompany, setEscrowCompany] = useState(saved.escrowCompany ?? 'First American Title')
  const [escrowOfficer, setEscrowOfficer] = useState(saved.escrowOfficer ?? '')
  const [escrowPhone, setEscrowPhone] = useState(saved.escrowPhone ?? '')
  const [attorney, setAttorney] = useState(saved.attorney ?? '')
  const [inspector, setInspector] = useState(saved.inspector ?? '')
  const [closeDate, setCloseDate] = useState(saved.closeDate ?? addDays(today(), 45))
  const [ddDeadline, setDdDeadline] = useState(saved.ddDeadline ?? addDays(today(), 21))
  const [earnestDue, setEarnestDue] = useState(saved.earnestDue ?? addDays(today(), 3))
  const [escrowOpened, setEscrowOpened] = useState(saved.escrowOpened ?? false)
  const [escrowNumber, setEscrowNumber] = useState(saved.escrowNumber ?? '')
  const [showTimeline, setShowTimeline] = useState(false)

  // ── Milestone state ──
  const [completedIds, setCompletedIds] = useState(() => {
    if (saved.completedIds) return saved.completedIds
    const ids = []
    if (loi.documentId) ids.push('loiSubmitted', 'loiAccepted')
    if (psa.documentId) ids.push('psaSigned')
    return ids
  })

  // ── Task 10: Custom checklist (from admin upload) ──
  const [customChecklist, setCustomChecklist] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dd_custom_checklist') || '[]') } catch { return [] }
  })
  const [checklistUploading, setChecklistUploading] = useState(false)

  const handleChecklistUpload = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setChecklistUploading(true)
    const items = await parseChecklistFile(file)
    setCustomChecklist(items)
    localStorage.setItem('dd_custom_checklist', JSON.stringify(items))
    setChecklistUploading(false)
  }, [])

  const toggleCustomItem = useCallback((id) => {
    setCustomChecklist(prev => {
      const updated = prev.map(item => item.id === id ? { ...item, done: !item.done } : item)
      localStorage.setItem('dd_custom_checklist', JSON.stringify(updated))
      return updated
    })
  }, [])

  // ── Title checks ──
  const [titleDone, setTitleDone] = useState(saved.titleDone ?? [])

  // ── Email draft ──
  const [emailTab, setEmailTab] = useState('earnest')
  const [copiedEmail, setCopiedEmail] = useState(false)
  // Fix 9: track per-tab body/subject edits
  const [emailBodyOverrides, setEmailBodyOverrides] = useState(saved.emailBodyOverrides ?? {})
  const [emailSubjectOverrides, setEmailSubjectOverrides] = useState(saved.emailSubjectOverrides ?? {})

  // ── Persist escrow workflow state back to dealStore on every change ──
  useEffect(() => {
    updateDealState({
      escrowWorkflow: {
        escrowCompany, escrowOfficer, escrowPhone, attorney, inspector,
        closeDate, ddDeadline, earnestDue,
        escrowOpened, escrowNumber,
        completedIds, titleDone,
        emailBodyOverrides, emailSubjectOverrides,
      }
    })
  }, [escrowCompany, escrowOfficer, escrowPhone, attorney, inspector,
      closeDate, ddDeadline, earnestDue, escrowOpened, escrowNumber,
      completedIds, titleDone, emailBodyOverrides, emailSubjectOverrides]) // eslint-disable-line

  const statuses = useMemo(() => getMilestoneStatuses(completedIds), [completedIds])
  const progress = useMemo(() => calcProgress(completedIds), [completedIds])
  const riskData = useMemo(() => buildRiskAnalysis(completedIds, escrowOpened), [completedIds, escrowOpened])
  const timeline = useMemo(() => buildTimeline(completedIds, today(), propName), [completedIds, propName])

  const countdownData = useMemo(() => {
    const now = new Date()
    const close = new Date(closeDate + 'T12:00:00')
    const dd = new Date(ddDeadline + 'T12:00:00')
    const em = new Date(earnestDue + 'T12:00:00')
    const daysToClose = Math.max(0, Math.round((close - now) / 86400000))
    const ddDaysLeft = Math.max(0, Math.round((dd - now) / 86400000))
    const earnestDaysLeft = Math.max(0, Math.round((em - now) / 86400000))
    return { daysToClose, ddDaysLeft, earnestDaysLeft }
  }, [closeDate, ddDeadline, earnestDue])

  const EMAIL_TEMPLATES = {
    earnest: {
      label: '💰 Earnest Money Notice',
      subject: `Earnest Money Deposit — ${propName}`,
      body: `Hi ${escrowOfficer || '[Escrow Officer]'},\n\nPlease confirm receipt of our earnest money deposit of ${money(earnestAmt)} for the above-referenced property.\n\nEscrow Company: ${escrowCompany}\nDD Deadline: ${fmtDate(ddDeadline)}\nEstimated Close: ${fmtDate(closeDate)}\n\nPlease send wire instructions at your earliest convenience. We will initiate the wire within 24 hours of receipt.\n\nThank you,\nAcquireOS AI Acquisitions LLC`,
    },
    dd: {
      label: '🔍 DD Extension Request',
      subject: `Due Diligence Extension Request — ${propName}`,
      body: `Dear Seller / Listing Broker,\n\nWe are writing to formally request a 10-day extension of the due diligence period for the above property.\n\nCurrent DD Deadline: ${fmtDate(ddDeadline)}\nRequested Extension: ${fmtDate(addDays(ddDeadline, 10))}\n\nWe are actively working through financial review and physical inspections and require additional time to complete our analysis properly. We remain committed to closing by ${fmtDate(closeDate)}.\n\nPlease confirm your approval at your earliest convenience.\n\nThank you,\nAcquireOS AI Acquisitions LLC`,
    },
    status: {
      label: '📋 Closing Status Update',
      subject: `Closing Status Update — ${propName}`,
      body: `Dear Team,\n\nHere is a brief status update on our acquisition of ${propName}.\n\nOverall Progress: ${progress}%\nMilestones Completed: ${completedIds.length} of ${MILESTONES.length}\nDays to Close: ${countdownData.daysToClose}\n\nKey Dates:\n• Earnest Money Due: ${fmtDate(earnestDue)}\n• DD Deadline: ${fmtDate(ddDeadline)}\n• Estimated Close: ${fmtDate(closeDate)}\n\nTitle Checks Cleared: ${titleDone.length}/${TITLE_CHECKS.length}\n\nNext Milestone: ${riskData.nextMilestone}\n${riskData.nextStep}\n\nWe will send another update if anything changes. Please reach out with any questions.\n\nBest,\nAcquireOS AI Acquisitions LLC`,
    },
  }
  const activeEmail = EMAIL_TEMPLATES[emailTab]

  const handleToggleMilestone = (id) => {
    setCompletedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
    if (id === 'escrowOpened') setEscrowOpened(true)
  }

  const handleOpenEscrow = () => {
    const num = `ESC-${Date.now().toString().slice(-8)}`
    setEscrowNumber(num)
    setEscrowOpened(true)
    if (!completedIds.includes('escrowOpened')) {
      setCompletedIds((prev) => [...prev, 'loiSubmitted', 'loiAccepted', 'psaSigned', 'escrowOpened'])
    }
  }

  const handleToggleTitle = (id) => {
    setTitleDone((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])
  }

  const handleComplete = () => {
    const num = escrowNumber || `ESC-${Date.now().toString().slice(-8)}`
    updateDealState({
      dueDiligence: {
        customChecklist: customChecklist.filter(i => i.done).map(i => i.label),
      },
      escrowDetails: {
        escrowCompany,
        escrowNumber: num,
        earnestMoneyDeposited: completedIds.includes('earnestDeposited'),
        titleSearch: {
          chainOfTitle: titleDone.includes('chainOfTitle'),
          ownershipVerified: titleDone.includes('ownerVerified'),
          publicRecordChecks: titleDone.includes('taxStatus'),
        },
        titleIssues: { liens: [], taxes: [], legalEncumbrances: [] },
        titleCommitment: titleDone.includes('titleCommitment'),
        milestones: {
          escrowOpened: completedIds.includes('escrowOpened') ? today() : null,
          titleCleared: titleDone.length >= 4 ? today() : null,
          closingReady: completedIds.includes('closingScheduled') ? today() : null,
        },
        progressPercentage: progress,
        escrowOfficer,
        closeDate,
        ddDeadline,
        earnestDue,
        earnestAmount: earnestAmt,
        completedMilestones: completedIds,
        titleChecksCompleted: titleDone,
      },
    })
    updateAvailableModules()
    forceNavigateToModule(14)
  }

  const riskColor = (l) => l === 'High' ? '#0f2d5e' : l === 'Medium' ? '#1e3a8a' : '#1d4ed8'
  const riskBg = (l) => l === 'High' ? '#eff6ff' : l === 'Medium' ? '#eff6ff' : '#eff6ff'

  return (
    <div className="m12-page">

      {/* ── Header ── */}
      <div className="m12-header">
        <div className="m12-badge">Module 12</div>
        <div className="m12-header-text">
          <h1>Escrow &amp; Title Workflow</h1>
          <p>Track every milestone from PSA execution to closing. One dashboard for the entire escrow process.</p>
        </div>
        <div className="m12-header-stats">
          <div className="m12-stat">
            <span>Overall Progress</span>
            <strong style={{ color: progress >= 75 ? '#1d4ed8' : progress >= 40 ? '#1e3a8a' : '#3b82f6' }}>
              {progress}%
            </strong>
          </div>
          <div className="m12-stat">
            <span>Milestones Done</span>
            <strong>{completedIds.length} / {MILESTONES.length}</strong>
          </div>
        </div>
      </div>

      <div className="m12-grid">

        {/* ── Escrow Dashboard Card ── */}
        <section className="m12-dashboard-card">
          <div className="m12-title-row">
            <span>Escrow Dashboard</span>
            <strong className={escrowOpened ? 'pill-blue-strong' : 'pill-blue-soft'}>
              {escrowOpened ? 'Open' : 'Not Opened'}
            </strong>
          </div>

          {/* Progress ring */}
          <div className="m12-progress-ring-wrap">
            <svg viewBox="0 0 120 120" className="m12-ring-svg">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#dbeafe" strokeWidth="10" />
              <circle
                cx="60" cy="60" r="50" fill="none"
                stroke={progress >= 75 ? '#1d4ed8' : progress >= 40 ? '#1e3a8a' : '#3b82f6'}
                strokeWidth="10"
                strokeDasharray={`${2 * Math.PI * 50}`}
                strokeDashoffset={`${2 * Math.PI * 50 * (1 - progress / 100)}`}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
                style={{ transition: 'stroke-dashoffset 0.6s ease' }}
              />
            </svg>
            <div className="m12-ring-label">
              <span className="m12-ring-pct">{progress}%</span>
              <span className="m12-ring-sub">Complete</span>
            </div>
          </div>

          {/* Property summary */}
          <div className="m12-prop-summary">
            <div><span>Property</span><strong>{propName}</strong></div>
            <div><span>Purchase Price</span><strong>{money(price)}</strong></div>
            <div><span>Earnest Money</span><strong>{money(earnestAmt)}</strong></div>
            <div><span>Location</span><strong>{location || '—'}</strong></div>
          </div>

          {/* Open Escrow button */}
          {!escrowOpened ? (
            <button className="m12-open-btn" onClick={handleOpenEscrow}>
              🏦 Open Escrow
            </button>
          ) : (
            <div className="m12-escrow-num">
              <span>Escrow #</span>
              <strong>{escrowNumber}</strong>
            </div>
          )}

          {/* Key dates */}
          <div className="m12-dates-grid">
            <div>
              <label>Earnest Money Due</label>
              <input type="date" value={earnestDue} onChange={e => setEarnestDue(e.target.value)} className="m12-date-input" />
            </div>
            <div>
              <label>DD Deadline</label>
              <input type="date" value={ddDeadline} onChange={e => setDdDeadline(e.target.value)} className="m12-date-input" />
            </div>
            <div>
              <label>Est. Close Date</label>
              <input type="date" value={closeDate} onChange={e => setCloseDate(e.target.value)} className="m12-date-input" />
            </div>
          </div>

          <div className="m12-action-row">
            <button className="m12-timeline-btn" onClick={() => setShowTimeline(t => !t)}>
              {showTimeline ? '▲ Hide Timeline' : '▼ View Timeline'}
            </button>
          </div>

          {/* Timeline */}
          {showTimeline && (
            <div className="m12-timeline">
              <div className="m12-timeline-title">Deal Timeline</div>
              {timeline.map((ev, i) => (
                <div key={i} className="m12-tl-item">
                  <span className="m12-tl-icon">{ev.icon}</span>
                  <div className="m12-tl-body">
                    <strong>{ev.label}</strong>
                    <span>{fmtDate(ev.date)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Milestone Stepper ── */}
        <section className="m12-milestones-card">
          <div className="m12-title-row">
            <span>Milestone Tracker</span>
            <strong className="pill-blue-deep">{completedIds.length} / {MILESTONES.length} complete</strong>
          </div>
          <p className="m12-milestones-hint">Click any milestone to mark as completed. Progress auto-updates.</p>

          <div className="m12-steps">
            {MILESTONES.map((m, i) => {
              const status = statuses[i]
              return (
                <button
                  key={m.id}
                  className={`m12-step ${status}`}
                  onClick={() => handleToggleMilestone(m.id)}
                >
                  <div className="m12-step-connector" />
                  <div className="m12-step-dot">
                    {status === STATUS.done ? '✓' : status === STATUS.active ? '●' : '○'}
                  </div>
                  <div className="m12-step-body">
                    <div className="m12-step-label">
                      <span>{m.icon} {m.label}</span>
                      <span className={`m12-step-badge ${status}`}>
                        {status === STATUS.done ? 'Completed' : status === STATUS.active ? 'In Progress' : 'Pending'}
                      </span>
                    </div>
                    <p className="m12-step-desc">{m.desc}</p>
                    {status === STATUS.done && <div className="m12-step-date">✓ {fmtDate(today())}</div>}
                  </div>
                </button>
              )
            })}
          </div>
        </section>

        {/* ── AI Risk Radar ── */}
        <section className="m12-risk-card">
          <div className="m12-title-row">
            <span>AI Risk Radar</span>
            <strong style={{
              background: riskBg(riskData.overallRisk),
              color: riskColor(riskData.overallRisk),
              borderRadius: 999, padding: '4px 10px', fontSize: 10, fontWeight: 900
            }}>
              {riskData.overallRisk} Risk
            </strong>
          </div>

          <div className="m12-next-step">
            <div className="m12-next-label">Next Step</div>
            <div className="m12-next-milestone">{riskData.nextMilestone}</div>
            <p className="m12-next-desc">{riskData.nextStep}</p>
          </div>

          {riskData.risks.length > 0 && (
            <div className="m12-risk-list">
              {riskData.risks.map((r, i) => (
                <div key={i} className="m12-risk-row" style={{ borderLeft: `3px solid ${riskColor(r.level)}` }}>
                  <div className="m12-risk-header-row">
                    <span className="m12-risk-tag" style={{ background: riskBg(r.level), color: riskColor(r.level) }}>
                      {r.level}
                    </span>
                    <span className="m12-risk-label">{r.label}</span>
                  </div>
                  <p className="m12-risk-detail">{r.detail}</p>
                </div>
              ))}
            </div>
          )}

          {riskData.risks.length === 0 && (
            <div className="m12-no-risk">
              <span>🎉</span>
              <p>No critical risks detected. Deal is on track for closing!</p>
            </div>
          )}

          {/* Key dates summary */}
          <div className="m12-dates-summary">
            <div><span>Earnest Due</span><strong>{fmtDate(earnestDue)}</strong></div>
            <div><span>DD Deadline</span><strong>{fmtDate(ddDeadline)}</strong></div>
            <div><span>Close Date</span><strong>{fmtDate(closeDate)}</strong></div>
          </div>
        </section>

        {/* ── Contacts Panel ── */}
        <section className="m12-contacts-card">
          <div className="m12-title-row">
            <span>Escrow &amp; Title Contacts</span>
            <strong className="pill-blue-soft">Key Parties</strong>
          </div>

          <div className="m12-contacts-grid">
            <div className="m12-contact-field">
              <label>🏦 Title / Escrow Company</label>
              <input className="m12-input" value={escrowCompany} onChange={e => setEscrowCompany(e.target.value)} placeholder="First American Title" />
            </div>
            <div className="m12-contact-field">
              <label>👤 Escrow Officer</label>
              <input className="m12-input" value={escrowOfficer} onChange={e => setEscrowOfficer(e.target.value)} placeholder="Name + phone" />
            </div>
            <div className="m12-contact-field">
              <label>📞 Escrow Phone</label>
              <input className="m12-input" value={escrowPhone} onChange={e => setEscrowPhone(e.target.value)} placeholder="(555) 000-0000" />
            </div>
            <div className="m12-contact-field">
              <label>⚖️ Real Estate Attorney</label>
              <input className="m12-input" value={attorney} onChange={e => setAttorney(e.target.value)} placeholder="Attorney name" />
            </div>
            <div className="m12-contact-field">
              <label>🔍 Property Inspector</label>
              <input className="m12-input" value={inspector} onChange={e => setInspector(e.target.value)} placeholder="Inspector name" />
            </div>
          </div>
        </section>

        {/* ── Days-to-Close Countdown ── */}
        <section className="m12-countdown-card">
          <div className="m12-title-row">
            <span>Days-to-Close Countdown</span>
            <strong className={countdownData.daysToClose <= 7 ? 'pill-blue-risk' : countdownData.daysToClose <= 14 ? 'pill-blue-mid' : 'pill-blue-deep'}>
              {countdownData.daysToClose}d to close
            </strong>
          </div>
          <div className="m12-countdown-grid">
            {[
              { label: 'Days to Close', value: countdownData.daysToClose, date: fmtDate(closeDate), urgent: countdownData.daysToClose <= 7, icon: '🏁' },
              { label: 'DD Deadline', value: countdownData.ddDaysLeft, date: fmtDate(ddDeadline), urgent: countdownData.ddDaysLeft <= 3, icon: '🔍' },
              { label: 'Earnest Due', value: countdownData.earnestDaysLeft, date: fmtDate(earnestDue), urgent: countdownData.earnestDaysLeft <= 2, icon: '💰' },
            ].map((item) => (
              <div key={item.label} className={`m12-countdown-item ${item.urgent ? 'urgent' : ''}`}>
                <div className="m12-cd-icon">{item.icon}</div>
                <div className="m12-cd-value">{item.value}</div>
                <div className="m12-cd-unit">days</div>
                <div className="m12-cd-label">{item.label}</div>
                <div className="m12-cd-date">{item.date}</div>
              </div>
            ))}
          </div>
          <div className="m12-countdown-bar-wrap">
            <div className="m12-countdown-bar" style={{ width: `${progress}%` }} />
          </div>
          <div className="m12-countdown-hint">Overall escrow progress: {progress}%</div>
        </section>

        {/* ── Email Draft Generator ── */}
        <section className="m12-email-card">
          <div className="m12-title-row">
            <span>Email Draft Generator</span>
            <strong className="pill-blue-soft">3 Templates</strong>
          </div>
          <div className="m12-email-tabs">
            {Object.entries(EMAIL_TEMPLATES).map(([key, tpl]) => (
              <button
                key={key}
                className={`m12-email-tab-btn ${emailTab === key ? 'active' : ''}`}
                onClick={() => { setEmailTab(key); setCopiedEmail(false) }}
              >
                {tpl.label}
              </button>
            ))}
          </div>
          {/* Subject — editable */}
          <div className="m12-email-subject">
            <span>Subject:</span>
            <input
              className="m12-email-subject-input"
              type="text"
              value={emailSubjectOverrides[emailTab] ?? activeEmail.subject}
              onChange={e => setEmailSubjectOverrides(prev => ({ ...prev, [emailTab]: e.target.value }))}
            />
          </div>
          {/* Body — editable (Fix 9: was readOnly) */}
          <textarea
            className="m12-email-body"
            value={emailBodyOverrides[emailTab] ?? activeEmail.body}
            onChange={e => setEmailBodyOverrides(prev => ({ ...prev, [emailTab]: e.target.value }))}
            rows={10}
          />
          <p className="m12-email-edit-hint">✎ Edit the subject or body above before copying or sending.</p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              className="m12-email-copy-btn"
              onClick={() => {
                const subj = emailSubjectOverrides[emailTab] ?? activeEmail.subject
                const body = emailBodyOverrides[emailTab] ?? activeEmail.body
                navigator.clipboard.writeText(`Subject: ${subj}\n\n${body}`).then(() => {
                  setCopiedEmail(true)
                  setTimeout(() => setCopiedEmail(false), 1800)
                })
              }}
            >
              {copiedEmail ? '✓ Copied to Clipboard' : '📋 Copy Email Draft'}
            </button>
            <a
              className="m12-email-copy-btn"
              href={`mailto:?subject=${encodeURIComponent(emailSubjectOverrides[emailTab] ?? activeEmail.subject)}&body=${encodeURIComponent(emailBodyOverrides[emailTab] ?? activeEmail.body)}`}
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              title="Open this email draft in your mail client"
            >
              ✉️ Open in Mail
            </a>
          </div>
        </section>

      </div>

      <div className="module-bottom-advance">
        <button className="m12-advance-btn" onClick={handleComplete}>
          Finalize Escrow &amp; Advance to Module 13
        </button>
      </div>
    </div>
  )
}

export default Module12Page
